import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useBookApi } from '../hooks/useBookApi';
import { Book } from '../services/bookService';
import BookCard from '../components/common/BookCard';
import { BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';

// Grade options
const gradeOptions = [
  { value: 'prep3', label: 'الصف الثالث الأعدادي' },
  { value: 'أولى ثانوي', label: 'أولى ثانوي' },
  { value: 'تانية ثانوي', label: 'تانية ثانوي' },
  { value: 'تالتة ثانوي', label: 'تالتة ثانوي' },
];

// Loading shimmer component
const BookCardShimmer = () => (
  <div className="bg-white rounded-2xl shadow-md border border-slate-100 p-6 animate-pulse">
    <div className="bg-gray-200 rounded-xl h-48 mb-4"></div>
    <div className="space-y-3">
      <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      <div className="h-4 bg-gray-200 rounded w-full"></div>
      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
      <div className="flex justify-between items-center mt-6">
        <div className="h-8 bg-gray-200 rounded w-24"></div>
        <div className="h-10 bg-gray-200 rounded w-20"></div>
      </div>
    </div>
  </div>
);

// Empty state component
const EmptyState = () => (
  <div className="col-span-full text-center py-12">
    <div className="inline-block p-4 bg-blue-100 rounded-full mb-4">
      <BookOpen className="w-8 h-8 text-blue-600" />
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد كتب متاحة</h3>
    <p className="text-gray-500">لا توجد كتب متاحة حالياً لهذه المرحلة الدراسية.</p>
  </div>
);

// Error state component
const ErrorState = ({ error, onRetry }: { error: string; onRetry: () => void }) => (
  <div className="col-span-full text-center py-12">
    <div className="inline-block p-4 bg-blue-100 rounded-full mb-4">
      <BookOpen className="w-8 h-8 text-blue-600" />
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-2">حدث خطأ</h3>
    <p className="text-gray-500 mb-4">{error}</p>
    <button
      onClick={onRetry}
      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
    >
      إعادة المحاولة
    </button>
  </div>
);

// Pagination component
const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange 
}: { 
  currentPage: number; 
  totalPages: number; 
  onPageChange: (page: number) => void; 
}) => {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="flex justify-center items-center gap-2 mt-8">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
      
      {getPageNumbers().map((page, index) => (
        <button
          key={index}
          onClick={() => typeof page === 'number' && onPageChange(page)}
          disabled={typeof page !== 'number'}
          className={`px-3 py-2 rounded-lg ${
            page === currentPage
              ? 'bg-blue-600 text-white'
              : typeof page === 'number'
              ? 'border border-gray-300 hover:bg-gray-50'
              : 'cursor-default'
          }`}
        >
          {page}
        </button>
      ))}
      
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
    </div>
  );
};

const BookGradePage: React.FC = () => {
  const { grade } = useParams<{ grade: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [books, setBooks] = useState<Book[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  const { isLoading, error, getActiveBooksByGrade } = useBookApi();
  
  const pageSize = 8; // Number of books per page

  // Get grade label
  const getGradeLabel = (gradeValue: string) => {
    const gradeMap: { [key: string]: string } = {
      'Secondary1': 'أولى ثانوي',
      'Secondary2': 'تانية ثانوي',
      'Secondary3': 'تالتة ثانوي',
      'أولى ثانوي': 'أولى ثانوي',
      'تانية ثانوي': 'تانية ثانوي',
      'تالتة ثانوي': 'تالتة ثانوي',
      'secondary1': 'أولى ثانوي',
      'secondary2': 'تانية ثانوي',
      'secondary3': 'تالتة ثانوي'
    };
    return gradeMap[gradeValue] || gradeValue;
  };

  // Convert Arabic grade to English for API
  const getEnglishGrade = (arabicGrade: string) => {
    const gradeMap: { [key: string]: string } = {
      'أولى ثانوي': 'Secondary1',
      'تانية ثانوي': 'Secondary2',
      'تالتة ثانوي': 'Secondary3'
    };
    return gradeMap[arabicGrade] || arabicGrade;
  };

  // Load books when grade or page changes
  useEffect(() => {
    const loadBooks = async () => {
      if (!grade) return;
      
      try {
                 const response = await getActiveBooksByGrade(
           getEnglishGrade(grade) as 'Secondary1' | 'Secondary2' | 'Secondary3',
           currentPage,
           pageSize
         );
        
        if (response?.success && response.data) {
          setBooks(response.data.books || []);
          setTotalPages(response.data.totalPages || 1);
          setTotalCount(response.data.totalCount || 0);
        } else {
          setBooks([]);
          setTotalPages(1);
          setTotalCount(0);
        }
      } catch (error) {
        console.error('Error loading books:', error);
        setBooks([]);
        setTotalPages(1);
        setTotalCount(0);
      }
    };

    loadBooks();
  }, [grade, currentPage, getActiveBooksByGrade]);

  // Reset page when grade changes
  useEffect(() => {
    setCurrentPage(1);
  }, [grade]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of books section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRetry = () => {
    // Force a reload by changing the page temporarily
    setCurrentPage(1);
  };

  if (!grade) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">المرحلة الدراسية غير محددة</h2>
        <p className="text-gray-600">يرجى تحديد مرحلة دراسية صحيحة.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page Header */}
      <div className="text-center mb-8">
                 <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
           {getGradeLabel(grade)}
         </h1>
        <p className="text-lg text-gray-600">
          تصفح الكتب المتاحة لمرحلة {getGradeLabel(grade)}
        </p>
      </div>

      {/* Books Count */}
      {!isLoading && !error && (
        <div className="text-center mb-8">
          <p className="text-gray-600">
            {totalCount > 0 ? (
              <>
                عرض <span className="font-semibold text-blue-600">{books.length}</span> من أصل{' '}
                <span className="font-semibold text-blue-600">{totalCount}</span> كتاب
              </>
            ) : (
              'لا توجد كتب متاحة'
            )}
          </p>
        </div>
      )}

      {/* Books Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
        {isLoading ? (
          // Loading state
          Array.from({ length: pageSize }).map((_, index) => (
            <BookCardShimmer key={`shimmer-${index}`} />
          ))
        ) : error ? (
          // Error state
          <ErrorState error={error} onRetry={handleRetry} />
        ) : books.length === 0 ? (
          // Empty state
          <EmptyState />
        ) : (
          // Books list
          books.map((book) => (
            <BookCard key={book.id} book={book} />
          ))
        )}
      </div>

      {/* Pagination */}
      {!isLoading && !error && books.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default BookGradePage;
